import { format } from 'bytes';
import { roundHelper } from './round.helper.class.js';

type UnitType = 'imperial' | 'metric';

export class UnitConversionHelper {
	private readonly roundTo: number = 2;

	formatBytes(bytes: number, decimals = 2): string {
		return (
			format(bytes, {
				decimalPlaces: decimals
			}) ?? `${bytes}B`
		);
	}

	roundUnitValue(value: number, round: boolean = false): number {
		if (!round) {
			return value;
		}
		return roundHelper.roundTo(value, this.roundTo);
	}

	convertCmToFeetAndInches(value: number, round: boolean = true): string {
		const realFeet = (value * 0.3937) / 12;
		const feet = Math.floor(realFeet);
		const inches = Math.round((realFeet - feet) * 12);
		return `${feet} ft ${inches} in`;
	}

	convertCmToInches(cm: number, round: boolean = false): number {
		const value = cm / 2.54;

		if (!round) {
			return value;
		}
		return roundHelper.roundTo(value, this.roundTo);
	}

	convertInchesToCm(inches: number, round: boolean = false): number {
		const value = inches * 2.54;
		if (!round) {
			return value;
		}
		return roundHelper.roundTo(value, this.roundTo);
	}

	convertKgToLbs(kg: number, round: boolean = false): number {
		const value = kg * 2.2046;
		if (!round) {
			return value;
		}
		return roundHelper.roundTo(value, this.roundTo);
	}

	convertMetersToFeets(meters: number, round: boolean = false): number {
		const value = 3.280839895 * meters;
		if (!round) {
			return value;
		}
		return roundHelper.roundTo(value, this.roundTo);
	}

	convertFeetsToMeters(feets: number, round: boolean = false): number {
		const value = feets / 3.280839895;
		if (!round) {
			return value;
		}
		return roundHelper.roundTo(value, this.roundTo);
	}

	convertLbsToKg(lbs: number, round: boolean = false): number {
		const value = lbs / 2.2046;
		if (!round) {
			return value;
		}
		return roundHelper.roundTo(value, this.roundTo);
	}

	cmToFeet(cm: number): string {
		let inches = +(cm * 0.393700787).toFixed(0);
		const feet = Math.floor(inches / 12);
		inches %= 12;

		return `${feet} ft ${inches}"`;
	}

	heightInPreferredUnit(unit: UnitType | undefined, heightInCm: number | undefined, round: boolean = true): string {
		if (!unit) {
			unit = 'metric';
		}

		if (!heightInCm) {
			return ``;
		}
		if (unit === 'metric') {
			return `${round ? roundHelper.roundTo(heightInCm, this.roundTo) : heightInCm} cm`;
		}

		return this.convertCmToFeetAndInches(heightInCm);
	}

	weightInPreferredUnit(unit: UnitType | undefined, weightInKg: number | undefined, round: boolean = true): string {
		if (!unit) {
			unit = 'metric';
		}

		if (!weightInKg) {
			return ``;
		}
		if (unit === 'metric') {
			return `${round ? roundHelper.roundTo(weightInKg, this.roundTo) : weightInKg} kg`;
		}
		return `${this.convertKgToLbs(weightInKg, round)} lbs`;
	}
}

export const unitConversionHelper = new UnitConversionHelper();
